// «Yêu cầu hỗ trợ có cập nhật» trong chuông «Tin mới» — sếp Phú 12/10/2026: ticket khách gửi CNV
// (qua «Trợ giúp AI» = widget livechat của Core nhúng trên hub) có thông báo, «ví dụ như đổi trạng
// thái ticket», thì hiện ở chuông luôn, bấm vào là xem.
//
// Nguồn dữ liệu là CHÍNH widget (window.CnvLiveChat — kernel livechat-widget.js ≥ 0.170.0): widget
// đang giữ token yêu cầu của khách, danh sách «Yêu cầu của tôi» và mốc đã xem. Kho này KHÔNG gọi máy
// chủ nào, KHÔNG giữ bản sao quyền xem — chỉ hỏi widget và nghe sự kiện của nó:
//   cnv-livechat:ready    { tickets }                       — khởi động xong (cả sau reset đổi người)
//   cnv-livechat:tickets  { enabled, unread, tickets[] }    — danh sách / chấm «có tin mới» đã đổi
//   CnvLiveChat.refreshTickets({ force }) · openTickets(id) · markTicketsSeen(ids?)
// Host không nhúng widget (Core admin) hoặc widget đời cũ chưa có API ⇒ kho rỗng, chuông y như cũ.
//
// 13/10/2026: nút «Trợ giúp» dùng chung (support-help/HelpMenu) cũng đọc kho này — Site Connect không có
// chuông nên kho tự hỏi widget theo nhịp riêng (initSupportTicketFeed), không trông vào chuông.
//
// Kho module (useSyncExternalStore) treo trên globalThis — cùng lý do productNewsStore: chuông có
// thể mount hai nơi (thanh trên cùng + đáy rail), bundle có thể nạp hai lần, số phải là MỘT.
import { useSyncExternalStore } from 'react';

const EMPTY = Object.freeze({ enabled: false, scope: 'visitor', items: [], unread: 0 });

const G = (() => {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__cnvSupportTicketFeed) {
    g.__cnvSupportTicketFeed = { state: EMPTY, listeners: new Set(), inited: false };
  }
  return g.__cnvSupportTicketFeed;
})();

function emit() {
  for (const l of G.listeners) {
    try { l(); } catch { /* một listener lỗi không được chặn các listener khác */ }
  }
}

function setState(next) {
  G.state = next;
  emit();
}

export function getSupportTicketState() { return G.state; }

function subscribe(l) {
  G.listeners.add(l);
  return () => G.listeners.delete(l);
}

export function useSupportTickets() {
  return useSyncExternalStore(subscribe, getSupportTicketState, getSupportTicketState);
}

function widget() {
  return typeof window !== 'undefined' ? window.CnvLiveChat : undefined;
}

/**
 * Dòng cho chuông — CHỈ yêu cầu phía hỗ trợ đã đụng tới (trả lời / đổi trạng thái / giải quyết):
 * yêu cầu khách vừa tự gửi mà chưa ai xử lý không phải «thông báo». Mới nhất trước. Thuần dữ liệu.
 */
export function ticketNewsItems(tickets) {
  const out = [];
  for (const t of Array.isArray(tickets) ? tickets : []) {
    if (!t || !t.id || !t.staff_activity_at || Number.isNaN(Date.parse(t.staff_activity_at))) continue;
    const u = t.last_update && typeof t.last_update === 'object' ? t.last_update : null;
    out.push({
      id: String(t.id),
      code: t.code ? String(t.code) : '',
      title: t.title ? String(t.title) : '',
      status_label: t.status_label ? String(t.status_label) : '',
      status_tone: ['open', 'wait', 'done'].includes(t.status_tone) ? t.status_tone : 'open',
      at: t.staff_activity_at,
      kind: u && u.kind === 'reply' ? 'reply' : 'status',
      by: u && u.by ? String(u.by) : '',
      excerpt: u && u.excerpt ? String(u.excerpt) : '',
      requester_name: t.requester_name ? String(t.requester_name) : '',
      mine: t.mine !== false,
      has_news: !!t.has_news,
    });
  }
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

function apply(detail) {
  const items = ticketNewsItems(detail && detail.tickets);
  setState({
    enabled: !!(detail && detail.enabled),
    scope: detail && detail.scope === 'workspace' ? 'workspace' : 'visitor',
    items,
    unread: items.filter((i) => i.has_news).length,
  });
}

/**
 * Hỏi widget danh sách mới nhất. Widget tự chặn gọi dồn (60 giây một lượt, `force` bỏ qua) và không
 * ném; widget chưa có / đời cũ ⇒ giữ nguyên kho.
 */
export async function refreshSupportTickets({ force = false } = {}) {
  const w = widget();
  if (!w || typeof w.refreshTickets !== 'function') return G.state;
  try {
    apply(await w.refreshTickets({ force }));
  } catch { /* giữ bản đang có — chuông giữ số cũ còn hơn nhảy về 0 */ }
  return G.state;
}

const POLL_MS = 5 * 60 * 1000;

/**
 * Nghe widget + tự hỏi theo nhịp (5 phút khi tab đang hiện · khi quay lại tab) — gọi bao nhiêu lần cũng
 * chỉ gắn một lần. Widget tự chặn gọi dồn (60 giây một lượt) nên chuông + nút Trợ giúp cùng hỏi cũng
 * chỉ một lượt thật tới máy chủ hỗ trợ.
 */
export function initSupportTicketFeed() {
  if (G.inited || typeof window === 'undefined') return;
  G.inited = true;
  window.addEventListener('cnv-livechat:tickets', (e) => apply(e && e.detail));
  // Widget nạp KHÔNG đồng bộ (sau đăng nhập) — khởi động xong mới hỏi được.
  window.addEventListener('cnv-livechat:ready', () => { refreshSupportTickets({ force: true }); });
  window.addEventListener('focus', () => { refreshSupportTickets(); });
  setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') refreshSupportTickets();
  }, POLL_MS);
  if (widget()?.ready) refreshSupportTickets();
}

/** Mở khung hỗ trợ ngay tại yêu cầu đó (widget tự đánh dấu đã xem khi tải xong). false = không có widget. */
export function openSupportTicket(id) {
  const w = widget();
  if (!id || !w || typeof w.openTickets !== 'function') return false;
  w.openTickets(String(id));
  return true;
}

/** Nút «Đánh dấu đã đọc» của chuông — đánh dấu mọi yêu cầu đang có. */
export function markAllSupportTicketsSeen() {
  const w = widget();
  if (w && typeof w.markTicketsSeen === 'function') w.markTicketsSeen();
}
